import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SnackbarService } from '../services/snackbar.service';
import { LoginService } from '../services/login.service';
import { EncryptionService } from './encryption.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private snackbarService: SnackbarService,
        private loginService: LoginService,
        private encryptionService: EncryptionService
    ) {}

    // Helper function to check if a string looks like encrypted data (iv:ciphertext format)
    private isEncryptedData(data: any): boolean {
        return typeof data === 'string' && 
               data.includes(':') && 
               data.split(':').length === 2 &&
               data.split(':')[0].length === 32 && // IV should be 32 hex chars (16 bytes)
               data.split(':')[1].length > 0;     // Ciphertext should exist
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((err: HttpErrorResponse) => {
                // Check if error message is encrypted and decrypt it
                if (this.isEncryptedData(err.error)) {
                    console.log('Encrypted error detected, attempting to decrypt...');
                    
                    return from(this.encryptionService.decryptData(err.error)).pipe(
                        switchMap(decryptedError => {
                            console.log('Decrypted error:', decryptedError);
                            
                            // Create new error with decrypted message
                            const newError = new HttpErrorResponse({
                                error: decryptedError,
                                headers: err.headers,
                                status: err.status,
                                statusText: err.statusText,
                                url: err.url ?? undefined
                            });
                            
                            // Handle the decrypted error - pass true flag to indicate it's decrypted
                            this.handleError(newError, true);
                            return throwError(() => newError);
                        }),
                        catchError(decryptError => {
                            console.error('Failed to decrypt error message:', decryptError);
                            console.error('%c[API ERROR] Original encrypted error:', 'color: red; font-weight: bold;', err);
                            
                            // Fallback to original error handling
                            this.handleError(err, false);
                            return throwError(() => err);
                        })
                    );
                } else {
                    // Handle non-encrypted errors normally
                    this.handleError(err, false);
                    return throwError(() => err);
                }
            })
        );
    }

    private handleError(err: HttpErrorResponse, isDecrypted: boolean = false): void {
        // Log the full error object to console for debugging
        console.error('%c[API ERROR] Full Response:', 'color: red; font-weight: bold;', err);
        if (err.error && !this.isEncryptedData(err.error)) {
            console.error('%c[API ERROR] Backend Message:', 'color: orange;', err.error);
        }

        if (err.status === 401) {
            this.router.navigate([`${''}/signin`], { replaceUrl: true });
            localStorage.removeItem('token');
            // sessionStorage.removeItem('token');
        }
        else if (err.status === 403) {
            this.snackbarService.getMessage('Permission Denied!!');
        }
         else if (err.status === 500) {
            this.snackbarService.getMessage('Internally Server Error!!');
            // this.snackbarService.getMessage(err.error.message || 'Internally Server Error!!');
        }
        else if (err.status === 503) {
            this.snackbarService.getMessage('Service Unavailable: Please try again later.');
        }
        else if (err.status === 0) {
            this.snackbarService.getMessage('Server not reachable!!');
        }
        else if (err.status === 404) {
            this.snackbarService.getMessage('Not Found');
        }
       else if (err.status === 422) {
  let errorMessage = 'Validation Error';

  if (!this.isEncryptedData(err.error)) {
    if (err.error && typeof err.error === 'object') {
      // Prefer backend-provided message
      errorMessage =
        (err.error.errors && err.error.errors[0]?.msg) ||err.error.message ||
         err.error?.message || 
        "Something went wrong";
    } else if (typeof err.error === 'string') {
      errorMessage = err.error;
    } else if (err.message) {
      errorMessage = err.message;
    }

    this.snackbarService.getMessage(errorMessage);
  }
}

        else if (err.status === 901) {
            this.snackbarService.getMessage('Permission changed!!');

            // const sessionData = sessionStorage.getItem('token');
            const sessionData = localStorage.getItem('token');
            const data = sessionData ? JSON.parse(sessionData) : '';
            const user = data?.data?.user;

            if (user?._id) {
                this.loginService.updateUserPermission(user._id).subscribe({
                    next: (res: any) => {
                        window.location.reload();
                    },
                    error: (updateErr) => {
                        console.error('%c[API ERROR] Permission Update Failed:', 'color: red;', updateErr);
                    }
                });
            }
        }
    }
}